import React, { useState, useRef } from "react";
import { PaperClip } from "../../icons/svg/outline";
import Button from "../buttons/Button";

/**
 * Generic File Upload Component
 * 
 * A reusable component for file selection and validation.
 * Handles file selection, validation, and error states.
 * The parent component is responsible for the actual upload.
 * 
 * @param {Function} onUploadSuccess - Callback when files are selected successfully
 * @param {Function} onUploadError - Callback when file selection fails
 * @param {Array} pendingFiles - Array of File objects that are pending upload
 * @param {Function} onRemoveFile - Callback when a file should be removed from pending list (receives file index)
 * @param {boolean} disabled - Whether the upload is disabled
 * @param {string} buttonText - Custom button text (default: "Upload File")
 * @param {string} buttonVariant - Button variant (default: "secondary")
 * @param {string} acceptedFileTypes - Accept attribute for file input (default: "*")
 * @param {boolean} multiple - Allow multiple file selection (default: true)
 * @param {string} pendingFilesLabel - Label text for pending files list (default: "Files to attach")
 * @param {string} pendingFilesHelpText - Help text shown below pending files (optional)
 * @returns {JSX.Element} File picker and pending-file list.
 */
const FileUpload = ({
  onUploadSuccess,
  onUploadError,
  pendingFiles = [],
  onRemoveFile,
  disabled = false,
  buttonText = "Upload File",
  buttonVariant = "secondary",
  acceptedFileTypes = "*",
  multiple = true,
  pendingFilesLabel = "Files to attach",
  pendingFilesHelpText,
}) => {
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  /**
   * Validates selected files and passes them to `onUploadSuccess`.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e File input change.
   * @returns {void}
   */
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Validate file types if acceptedFileTypes is specified and not "*"
    if (acceptedFileTypes && acceptedFileTypes !== "*") {
      const invalidFiles = files.filter((file) => {
        const isImage = file.type.startsWith("image/");
        const isPDF = file.type === "application/pdf";
        const isVideo = file.type.startsWith("video/");
        
        // Check if file type matches accepted types
        if (acceptedFileTypes.includes("image/*") && isImage) return false;
        if (acceptedFileTypes.includes("application/pdf") && isPDF) return false;
        if (acceptedFileTypes.includes("video/*") && isVideo) return false;
        
        // If none match, file is invalid
        return true;
      });

      if (invalidFiles.length > 0) {
        const fileNames = invalidFiles.map((f) => f.name).join(", ");
        setError(
          `Invalid file type(s): ${fileNames}. Only images and PDF files are allowed.`
        );
        onUploadError?.(new Error(`Invalid file types: ${fileNames}`));
        return;
      }
    }

    // Clear any previous errors
    setError(null);

    // Just store files - don't upload yet
    // The parent component will handle the actual upload
    onUploadSuccess?.(files);
    
    // Reset file input after processing so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  /**
   * Opens the hidden file input unless the control is disabled.
   *
   * @param {React.MouseEvent} e Button click.
   * @returns {void}
   */
  const handleButtonClick = (e) => {
    if (disabled) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    if (fileInputRef.current) {
      fileInputRef.current.click();
    } else {
      console.error("FileUpload: fileInputRef is not available");
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileSelect}
          disabled={disabled}
          accept={acceptedFileTypes}
          multiple={multiple}
          className="hidden"
        />
        <Button
          variant={buttonVariant}
          text={buttonText}
          onClick={handleButtonClick}
          disabled={disabled}
          icon={<PaperClip className="w-4 h-4" />}
          className="p-4"
        />
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2">
          {error}
        </div>
      )}

      {pendingFiles.length > 0 && (
        <div className="mt-2 space-y-1">
          <p className="text-xs font-medium text-gray-700">
            {pendingFilesLabel} ({pendingFiles.length}):
          </p>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {pendingFiles.map((file, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-2 bg-gray-50 border border-gray-200 rounded-md text-xs"
              >
                <span className="text-gray-700 truncate flex-1">
                  {file.name}
                </span>
                {onRemoveFile && (
                  <button
                    type="button"
                    onClick={() => onRemoveFile(index)}
                    className="ml-2 text-red-600 hover:text-red-800 text-xs font-medium"
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
          </div>
          {pendingFilesHelpText && (
            <p className="text-xs text-gray-500 mt-1">
              {pendingFilesHelpText}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default FileUpload;
